import { toast } from "@/components/ToastNotification";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useMutation } from "@tanstack/react-query";
import { Eye, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import ExamesSolicitacao from "../components/examesSolicitacao";
import { StatusSelector } from "../components/StatusSelector";
import VisualizadorArquivo from "../components/visualizadorArquivo";
import { StatusAtendimento } from "../constants/statusAtendimento";
import { AnaminesePreOperatoriaProps } from "../interfaces/anaminesePreOperatoria.interface";
import { AnaminesePreOperatoriaFormData, getInitialData } from "../models/anaminesePreOperatoria.model";
import { buscarPdfParaVisualizacao } from "../utils/fetchPdfParaVisualizacao";
import { saveFormProtocolo } from "../utils/formSaveHelper";
import { CampoObrigatorio, validarCamposObrigatorios } from "../utils/formValidation";

export const AnaminesePreOperatoria = ({ onDataChange, initialData, atendimentoId, pacienteId, empresaId }: AnaminesePreOperatoriaProps) => {
  const [formData, setFormData] = useState<AnaminesePreOperatoriaFormData>(() => {
    const initialFormData = getInitialData(initialData);
    // Se não houver status_atendimento nos dados iniciais, definir como 'Novo'
    if (!initialFormData.status_atendimento) {
      return { ...initialFormData, status_atendimento: StatusAtendimento.NOVO };
    }
    return initialFormData;
  });
  const [statusAtendimento, setStatusAtendimento] = useState<string | null>(() => {
    const initialFormData = getInitialData(initialData);
    return initialFormData.status_atendimento || StatusAtendimento.NOVO;
  });
  const [visualizadorAberto, setVisualizadorAberto] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [protocoloId, setProtocoloId] = useState<number | undefined>(initialData?.id);

  // Sincronizar protocoloId quando initialData mudar
  useEffect(() => {
    if (initialData?.id) {
      setProtocoloId(initialData.id);
    }
  }, [initialData?.id]);

  // Mutation para salvar o formulário
  const saveMutation = useMutation({
    mutationFn: async (data: AnaminesePreOperatoriaFormData) => {
      return await saveFormProtocolo({
        endpoint: '/api/protocolo-anamnese-pre-operatoria',
        data,
        atendimentoId: atendimentoId!,
        pacienteId: pacienteId || (initialData as any)?.id_paciente,
        empresaId: empresaId || (initialData as any)?.id_empresa,
        protocoloId: protocoloId || initialData?.id,
        statusAtendimento,
        formName: 'Anamnese Pré-Operatória'
      });
    },
    onSuccess: (response) => {
      // Extrair ID do response (pode estar em response.result.id ou response.data.result.id)
      const savedId = response?.result?.id || response?.data?.result?.id || protocoloId || initialData?.id;
      if (savedId && savedId !== protocoloId) {
        setProtocoloId(savedId);
        // Atualizar initialData através do callback
        onDataChange?.({ ...formData, id: savedId } as any);
      }
      
      // Disparar evento para atualizar painel de exames e documentos
      if (atendimentoId) {
        const event = new CustomEvent('examesAtualizados', {
          detail: { atendimentoId }
        });
        window.dispatchEvent(event);
      }
      
      toast.success("Formulário salvo com sucesso!");
    },
    onError: (error: any) => {
      console.error('Erro ao salvar anamnese pré-operatória:', error);
      toast.error("Não foi possível salvar o formulário.");
    }
  });

  const handleInputChange = useCallback((field: string, value: string) => {
    setFormData((prevData) => {
      const newData = { 
        ...prevData, 
        [field]: value,
        // Sempre manter o status_atendimento no formData se ele estiver definido
        ...(statusAtendimento && { status_atendimento: statusAtendimento })
      };
      const dataWithStatus = statusAtendimento 
        ? { ...newData, status_atendimento: statusAtendimento } as any
        : newData;
      onDataChange?.(dataWithStatus);
      return newData;
    });
  }, [onDataChange, statusAtendimento]);

  const handleSwitchChange = useCallback((field: string, checked: boolean) => {
    setFormData((prevData) => {
      const newData = { 
        ...prevData, 
        [field]: checked,
        // Sempre manter o status_atendimento no formData se ele estiver definido
        ...(statusAtendimento && { status_atendimento: statusAtendimento })
      };
      const dataWithStatus = statusAtendimento 
        ? { ...newData, status_atendimento: statusAtendimento } as any
        : newData;
      onDataChange?.(dataWithStatus);
      return newData;
    });
  }, [onDataChange, statusAtendimento]);

  const handleCheckboxChange = useCallback((field: string, checked: boolean) => {
    setFormData((prevData) => {
      const newData = { 
        ...prevData, 
        [field]: checked,
        // Sempre manter o status_atendimento no formData se ele estiver definido
        ...(statusAtendimento && { status_atendimento: statusAtendimento })
      };
      const dataWithStatus = statusAtendimento 
        ? { ...newData, status_atendimento: statusAtendimento } as any
        : newData;
      onDataChange?.(dataWithStatus);
      return newData;
    });
  }, [onDataChange, statusAtendimento]);

  const handleExamesChange = useCallback((exams: string[]) => {
    setFormData((prevData) => {
      const newData = { 
        ...prevData, 
        examesOftalmologicos: exams,
        // Sempre manter o status_atendimento no formData se ele estiver definido
        ...(statusAtendimento && { status_atendimento: statusAtendimento })
      };
      const dataWithStatus = statusAtendimento 
        ? { ...newData, status_atendimento: statusAtendimento } as any
        : newData;
      onDataChange?.(dataWithStatus);
      return newData;
    });
  }, [onDataChange, statusAtendimento]);

  const handleSave = useCallback(async () => {
    if (!atendimentoId) {
      toast.error("ID do atendimento não fornecido.");
      return;
    }

    // Definir campos obrigatórios para Anamnese Pré-Operatória
    // Nota: Segundo o schema do backend, todos os campos são opcionais
    const camposObrigatorios: CampoObrigatorio[] = [];

    const dataToSave = statusAtendimento 
      ? { ...formData, status_atendimento: statusAtendimento } as any
      : formData;

    // Validar campos obrigatórios
    if (!validarCamposObrigatorios(dataToSave, camposObrigatorios)) {
      return;
    }

    try {
      console.log('Salvando anamnese pré-operatória:', { endpoint: '/api/protocolo-anamnese-pre-operatoria', data: dataToSave, atendimentoId });
      await saveMutation.mutateAsync(dataToSave);
      console.log('Anamnese pré-operatória salva com sucesso');
    } catch (error) {
      console.error('Erro ao salvar anamnese pré-operatória:', error);
      toast.error("Não foi possível salvar o formulário. Verifique o console para mais detalhes.");
    }
  }, [atendimentoId, formData, statusAtendimento, saveMutation]);

  // Função para visualizar o PDF
  const handleVisualizar = useCallback(async () => {
    const currentProtocoloId = protocoloId || initialData?.id;
    
    if (!currentProtocoloId) {
      toast.warning("Salve o formulário antes de visualizar.");
      return;
    }

    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const url = `${BASE_URL}/api/protocolo-anamnese-pre-operatoria/${currentProtocoloId}/imprimir`;
      const blob = await buscarPdfParaVisualizacao({
        atendimentoId: atendimentoId,
        protocoloId: currentProtocoloId,
        tipoProtocolo: "anamnese_pre_operatoria",
        fallbackUrl: url
      });

      const urlBlob = window.URL.createObjectURL(blob);
      setPdfUrl(urlBlob);
      setVisualizadorAberto(true);
    } catch (error: any) {
      console.error('Erro ao gerar PDF:', error);
      toast.error(error?.message || "Não foi possível gerar o PDF.");
    }
  }, [protocoloId, initialData?.id, atendimentoId]);

  const fecharVisualizador = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setVisualizadorAberto(false);
    setPdfUrl(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-2 mb-4">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending 
            ? (protocoloId || initialData?.id ? 'Atualizando...' : 'Salvando...') 
            : (protocoloId || initialData?.id ? 'Atualizar' : 'Salvar')}
        </Button>
        <Button 
          variant="outline" 
          onClick={handleVisualizar}
          disabled={!protocoloId && !initialData?.id}
          title={!protocoloId && !initialData?.id ? "Salve o formulário antes de visualizar" : "Visualizar PDF"}
        >
          <Eye className="h-4 w-4 mr-2" />
          Visualizar Documento
        </Button>
      </div>
      <StatusSelector 
        atendimentoId={atendimentoId} 
        formName="Anamnese Pré-Operatória"
        value={statusAtendimento || formData.status_atendimento}
        onStatusSelect={(status) => {
          setStatusAtendimento(status || null);
          // Atualizar também no formData
          setFormData((prevData) => ({
            ...prevData,
            status_atendimento: status || undefined
          }));
          handleInputChange('status_atendimento', status || '');
        }}
      />
      {/* Card Histórico Clínico */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico Clínico</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 block">Comorbidades</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hipertensao"
                    checked={formData.hipertensao}
                    onCheckedChange={(checked) => handleCheckboxChange('hipertensao', checked as boolean)}
                  />
                  <Label htmlFor="hipertensao" className="font-normal cursor-pointer">
                    Hipertensão
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="diabetes"
                    checked={formData.diabetes}
                    onCheckedChange={(checked) => handleCheckboxChange('diabetes', checked as boolean)}
                  />
                  <Label htmlFor="diabetes" className="font-normal cursor-pointer">
                    Diabetes
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="doencaCardiaca"
                    checked={formData.doencaCardiaca}
                    onCheckedChange={(checked) => handleCheckboxChange('doencaCardiaca', checked as boolean)}
                  />
                  <Label htmlFor="doencaCardiaca" className="font-normal cursor-pointer">
                    Doença cardíaca
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="doencaRespiratoria"
                    checked={formData.doencaRespiratoria}
                    onCheckedChange={(checked) => handleCheckboxChange('doencaRespiratoria', checked as boolean)}
                  />
                  <Label htmlFor="doencaRespiratoria" className="font-normal cursor-pointer">
                    Doença respiratória
                  </Label>
                </div>
              </div>
            </div>

            <div>
              <Label>Outros</Label>
              <Textarea
                placeholder="Descreva outras comorbidades..."
                rows={2}
                value={formData.outrasComorbidades}
                onChange={(e) => handleInputChange('outrasComorbidades', e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label>Medicamentos em uso</Label>
            <Textarea
              placeholder="Liste os medicamentos que o paciente está utilizando..."
              rows={2}
              value={formData.medicamentosEmUso}
              onChange={(e) => handleInputChange('medicamentosEmUso', e.target.value)}
            />
          </div>

          <div>
            <Label>Alergias a medicamentos</Label>
            <Textarea
              placeholder="Descreva alergias medicamentosas conhecidas..."
              rows={2}
              value={formData.alergiasMedicamentos}
              onChange={(e) => handleInputChange('alergiasMedicamentos', e.target.value)}
            />
          </div>

          <div>
            <Label>Cirurgias anteriores</Label>
            <Textarea
              placeholder="Descreva cirurgias anteriores..."
              rows={2}
              value={formData.cirurgiasAnteriores}
              onChange={(e) => handleInputChange('cirurgiasAnteriores', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Card Histórico Oftalmológico */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico Oftalmológico</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Dados de início da perda visual</Label>
            <Textarea
              placeholder="Descreva quando e como começou a perda visual..."
              rows={2}
              value={formData.dadosInicioPerdaVisual}
              onChange={(e) => handleInputChange('dadosInicioPerdaVisual', e.target.value)}
            />
          </div>

          <div>
            <div className="flex items-center justify-between space-x-2 mb-2">
              <Label htmlFor="usoOculosLentes">Uso de óculos ou lentes:</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Não</span>
                <Switch
                  id="usoOculosLentes"
                  checked={formData.usoOculosLentes}
                  onCheckedChange={(checked) => handleSwitchChange('usoOculosLentes', checked)}
                />
                <span className="text-sm text-muted-foreground">Sim</span>
              </div>
            </div>
            {formData.usoOculosLentes && (
              <Input
                placeholder="Há quanto tempo"
                value={formData.tempoUsoOculosLentes}
                onChange={(e) => handleInputChange('tempoUsoOculosLentes', e.target.value)}
              />
            )}
          </div>

          <div>
            <Label>Diagnóstico de outras doenças oculares (glaucoma, retina, etc.)</Label>
            <Textarea
              placeholder="Descreva outras doenças oculares diagnosticadas..."
              rows={2}
              value={formData.diagnosticoDoencasOculares}
              onChange={(e) => handleInputChange('diagnosticoDoencasOculares', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Card Hábitos */}
      <Card>
        <CardHeader>
          <CardTitle>Hábitos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between space-x-2 mb-2">
              <Label htmlFor="fuma">Fuma:</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Não</span>
                <Switch
                  id="fuma"
                  checked={formData.fuma}
                  onCheckedChange={(checked) => handleSwitchChange('fuma', checked)}
                />
                <span className="text-sm text-muted-foreground">Sim</span>
              </div>
            </div>
            {formData.fuma && (
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Quantidade"
                  type="number"
                  value={formData.quantidadeCigarros}
                  onChange={(e) => handleInputChange('quantidadeCigarros', e.target.value)}
                />
                <Select
                  value={formData.frequenciaCigarros}
                  onValueChange={(value) => handleInputChange('frequenciaCigarros', value)}
                  onOpenChange={(open) => {}}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Frequência" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="diario">Diário</SelectItem>
                    <SelectItem value="semanal">Semanal</SelectItem>
                    <SelectItem value="mensal">Mensal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between space-x-2 mb-2">
              <Label htmlFor="consumoAlcool">Consumo de álcool:</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Não</span>
                <Switch
                  id="consumoAlcool"
                  checked={formData.consumoAlcool}
                  onCheckedChange={(checked) => handleSwitchChange('consumoAlcool', checked)}
                />
                <span className="text-sm text-muted-foreground">Sim</span>
              </div>
            </div>
            {formData.consumoAlcool && (
              <Input
                placeholder="Frequência"
                value={formData.frequenciaAlcool}
                onChange={(e) => handleInputChange('frequenciaAlcool', e.target.value)}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Card Avaliação Pré-Operatória */}
      <Card>
        <CardHeader>
          <CardTitle>Avaliação Pré-Operatória</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between space-x-2">
            <Label htmlFor="ocorrenciaAdversaAnestesia">Já apresentou ocorrência adversa à anestesia?</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="ocorrenciaAdversaAnestesia"
                checked={formData.ocorrenciaAdversaAnestesia}
                onCheckedChange={(checked) => handleSwitchChange('ocorrenciaAdversaAnestesia', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div className="flex items-center justify-between space-x-2">
            <Label htmlFor="dificuldadeMovimentacao">Dificuldade de movimentação ou outra limitação física?</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="dificuldadeMovimentacao"
                checked={formData.dificuldadeMovimentacao}
                onCheckedChange={(checked) => handleSwitchChange('dificuldadeMovimentacao', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card Sinais Vitais */}
      <Card>
        <CardHeader>
          <CardTitle>Sinais Vitais</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Pressão arterial</Label>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Ex: 120/80"
                  value={formData.pressaoArterial}
                  onChange={(e) => handleInputChange('pressaoArterial', e.target.value)}
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">mmHg</span>
              </div>
            </div>

            <div>
              <Label>Frequência cardíaca</Label>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Ex: 72"
                  type="number"
                  value={formData.frequenciaCardiaca}
                  onChange={(e) => handleInputChange('frequenciaCardiaca', e.target.value)}
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">bpm</span>
              </div>
            </div>

            <div>
              <Label>Temperatura</Label>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Ex: 36.5"
                  type="number"
                  step="0.1"
                  value={formData.temperatura}
                  onChange={(e) => handleInputChange('temperatura', e.target.value)}
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">°C</span>
              </div>
            </div>

            <div>
              <Label>Peso</Label>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Ex: 70"
                  type="number"
                  step="0.1"
                  value={formData.peso}
                  onChange={(e) => handleInputChange('peso', e.target.value)}
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">kg</span>
              </div>
            </div>

            <div>
              <Label>Altura</Label>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Ex: 175"
                  type="number"
                  step="0.1"
                  value={formData.altura}
                  onChange={(e) => handleInputChange('altura', e.target.value)}
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">cm</span>
              </div>
            </div>
          </div>

          <div>
            <Label>Exames Oftalmológicos Realizados</Label>
            <ExamesSolicitacao
              initialExams={formData.examesOftalmologicos}
              onExamsChange={handleExamesChange}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Acuidade visual OD</Label>
              <Input
                placeholder="Ex: 20/20"
                value={formData.acuidadeVisualOD}
                onChange={(e) => handleInputChange('acuidadeVisualOD', e.target.value)}
              />
            </div>
            <div>
              <Label>Acuidade visual OE</Label>
              <Input
                placeholder="Ex: 20/20"
                value={formData.acuidadeVisualOE}
                onChange={(e) => handleInputChange('acuidadeVisualOE', e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label>Biometria</Label>
            <Textarea
              placeholder="Descreva os resultados da biometria..."
              rows={2}
              value={formData.biometria}
              onChange={(e) => handleInputChange('biometria', e.target.value)}
            />
          </div>

          <div>
            <Label>Outros exames</Label>
            <Textarea
              placeholder="Descreva outros exames realizados..."
              rows={2}
              value={formData.outrosExames}
              onChange={(e) => handleInputChange('outrosExames', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Visualizador de PDF */}
      {pdfUrl && (
        <VisualizadorArquivo
          arquivoUrl={pdfUrl}
          nomeArquivo={`anamnese_pre_operatoria_${protocoloId || initialData?.id || 'novo'}.pdf`}
          tipoArquivo="pdf"
          open={visualizadorAberto}
          onClose={fecharVisualizador}
        />
      )}
    </div>
  );
};

export default AnaminesePreOperatoria;
